import { useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { Button, Typography } from '@/ui';

import { AssistantPageShell } from './AssistantPageShell';
import { AssistantRequestLinkForm } from './AssistantRequestLinkForm';

import type { AssistantExternalRef, AssistantExternalRefKind } from './api';
import type { FC, ReactNode } from 'react';

import { useGetCurrentUserSessionProbeQuery } from '@/store/api/userApi';
import { redirectToSignin } from '@/utils/authRedirect';

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu;

/**
 * The invite's id names the member, so a link goes to that member's
 * assistants. Any UUID is sent; Moments ignores one that names nothing. A
 * non-UUID id can't be an external ref at all (Moments would refuse the
 * request), so it is left off.
 */
export const inviteExternalRef = (
  kind: AssistantExternalRefKind,
  id: string | undefined,
): AssistantExternalRef | undefined =>
  id && UUID_PATTERN.test(id) ? { id: id.toLowerCase(), kind } : undefined;

const AnonymousInvite: FC<{ externalRef?: AssistantExternalRef }> = ({
  externalRef,
}) => {
  const { pathname, search } = useLocation();
  const [managesCalendar, setManagesCalendar] = useState(false);

  return (
    <AssistantPageShell>
      {managesCalendar ? (
        <div className="flex flex-col gap-4">
          <Typography variant="h4">Get a sign-in link</Typography>
          <Typography variant="body1">
            Enter the email you were added with as a scheduling assistant.
          </Typography>
          <AssistantRequestLinkForm externalRef={externalRef} />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <Typography variant="h4">Manage this Mento session</Typography>
          <Typography variant="body1">
            Sign in to reschedule or cancel. If you look after someone
            else&apos;s calendar, we&apos;ll email you a link instead.
          </Typography>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="contained"
              onClick={() => redirectToSignin(`${pathname}${search}`)}
            >
              Sign in
            </Button>
            <Button variant="outlined" onClick={() => setManagesCalendar(true)}>
              I manage this person&apos;s calendar
            </Button>
          </div>
        </div>
      )}
    </AssistantPageShell>
  );
};

interface InviteLandingProps {
  children: ReactNode;
  /** The route param holding the invite's id. */
  idParam: string;
  refKind: AssistantExternalRefKind;
}

/**
 * MEN-5598: the reschedule and cancel links in a calendar invite are also
 * opened by the member's scheduling assistant, who has no Mento account. A
 * probe of `/users/me` that does not redirect tells the two apart before the
 * page mounts: only a 401 shows the choice between signing in and asking for
 * an assistant link. Signed in, or any other answer, the page renders exactly
 * as before, so its private reads never fire for an anonymous visitor.
 */
export const InviteLanding: FC<InviteLandingProps> = ({
  children,
  idParam,
  refKind,
}) => {
  const params = useParams();
  const { error, isLoading } = useGetCurrentUserSessionProbeQuery();

  if (isLoading) {
    return <Typography>Loading session...</Typography>;
  }

  const status =
    error && typeof error === 'object' && 'status' in error
      ? (error as { status?: unknown }).status
      : undefined;
  if (status === 401) {
    return (
      <AnonymousInvite
        externalRef={inviteExternalRef(refKind, params[idParam])}
      />
    );
  }

  return children;
};
